import * as THREE from 'three';
import type { Renderer } from './Renderer';

/**
 * The sky: what the scene is drawn against, what fills its shadows, and what its distance fades to.
 *
 * ## Why one object owns all three
 *
 * They are the same fact seen three ways, and the only way they can disagree is by being set
 * separately. A flat blue clear colour with a neutral hemisphere light and no fog was three
 * independent guesses at "outdoors", and the tell was in the shadows: the sun is one light, so
 * everything it does not reach was lit only by a grey fill, and a shaded lawn came out grey-green
 * instead of the blue-shifted green that shade actually is outdoors.
 *
 * So the gradient is generated once per lighting state and then used as all three — background,
 * `Scene.environment` (via PMREM, so rough materials integrate the whole dome rather than sampling a
 * direction), and the fog colour. Shade is now lit by the sky that is visibly overhead, distance
 * fades to the horizon it actually meets, and moving the time of day moves all of it together.
 *
 * ## Why this is not a physical sky
 *
 * There is no Rayleigh/Mie scattering here, no sun disc, no clouds. This is a three-stop vertical
 * ramp whose stops are keyed off the sun's elevation. The goal (spec §18) is a stylised garden that
 * reads as the right time of day, not a photograph — and a physical sky model would cost a
 * screen-sized shader pass and a great deal of tuning to produce, at the elevations a garden is
 * usually viewed at, something a ramp gets within a few percent of.
 */

/** Sky state, keyed off the sun. */
export interface SkyState {
  /** Sun elevation in degrees; negative is below the horizon. */
  elevationDegrees: number;
}

/**
 * A ramp: zenith, horizon, and the ground haze below it.
 *
 * Three stops rather than two because the horizon is where a sky is least like a linear blend — it
 * brightens and desaturates over the last few degrees, which is most of what makes a gradient read
 * as sky rather than as a blue-to-white wash.
 */
interface SkyPalette {
  zenith: THREE.Color;
  horizon: THREE.Color;
  ground: THREE.Color;
  /**
   * How strongly the dome fills unlit surfaces. Below 1 deliberately: at 1 the sky bounce competes
   * with the sun and the whole scene flattens, which is the failure mode of adding IBL to a rig that
   * was already balanced without it.
   */
  intensity: number;
}

/** Elevation stops the palette is interpolated between. Below the first, it is night. */
interface SkyKeyframe {
  elevation: number;
  palette: SkyPalette;
}

/** Night. Not black — a moonless garden is still visibly blue, and black shadows read as holes. */
const NIGHT: SkyKeyframe = {
  elevation: -8,
  palette: {
    zenith: new THREE.Color(0x0b1626),
    horizon: new THREE.Color(0x1e2c40),
    ground: new THREE.Color(0x141b26),
    intensity: 0.25,
  },
};

/** Civil twilight: the horizon is the brightest thing in the sky and the zenith has gone deep. */
const TWILIGHT: SkyKeyframe = {
  elevation: 0,
  palette: {
    zenith: new THREE.Color(0x2f4a72),
    horizon: new THREE.Color(0xd2895c),
    ground: new THREE.Color(0x3a3730),
    intensity: 0.4,
  },
};

/** Golden hour. Warm enough to tint the lawn, which is the point of having it as its own stop. */
const GOLDEN: SkyKeyframe = {
  elevation: 10,
  palette: {
    zenith: new THREE.Color(0x5b86c4),
    horizon: new THREE.Color(0xe8b487),
    ground: new THREE.Color(0x6b6152),
    intensity: 0.55,
  },
};

/**
 * Full daylight. The horizon is deliberately not as pale as a real one: this sky is mostly seen as a
 * 25° band above the skyline, and a near-white band there reads as overcast haze and drains the
 * frame. Keeping some blue in it is what makes the garden look like it is outdoors on a clear day
 * rather than photographed against a lightbox.
 */
const DAY: SkyKeyframe = {
  elevation: 45,
  palette: {
    zenith: new THREE.Color(0x3d78c2),
    horizon: new THREE.Color(0xa8cbe8),
    ground: new THREE.Color(0x8a9080),
    intensity: 0.7,
  },
};

/** Ascending by elevation — {@link paletteFor} walks them in order and relies on it. */
const KEYFRAMES: readonly SkyKeyframe[] = [NIGHT, TWILIGHT, GOLDEN, DAY];

/**
 * Fog density (`FogExp2`).
 *
 * Tuned to be all but invisible across a garden and clearly present beyond it: a surface 40 m away
 * has lost a few percent of its contrast to haze and one 300 m away has lost most of it. That is the
 * ratio that separates a site from its backdrop, which is the whole job — without it a template's
 * distant terrain sits at the same contrast as the bed in front of the camera and the frame has no
 * depth at all.
 *
 * Set by eye against a screenshot, and lower than the first attempt. Fog reads far stronger against
 * a *pale* sky than the numbers suggest, because it is the sky colour that the distance fades to —
 * so the density and the horizon colour have to be tuned together, and getting the horizon right
 * meant needing less fog.
 */
const FOG_DENSITY = 0.0016;

/** Equirectangular gradient size. Tiny on purpose — it is three stops; a mip chain would resolve it. */
const SKY_WIDTH = 32;
const SKY_HEIGHT = 128;

/**
 * Elevation change, in degrees, that forces the environment to be re-prefiltered.
 *
 * PMREM convolution is a handful of GPU passes. It is cheap enough to do when the sun moves and far
 * too expensive to do every frame of a time-of-day scrub, and the palette is a smooth ramp, so
 * quantising the input to 2° makes a drag cost a couple of dozen rebuilds instead of hundreds while
 * staying well under what anyone can see between one frame and the next.
 */
const REBUILD_STEP_DEGREES = 2;

export class SkyEnvironment {
  private gradient: THREE.DataTexture;
  private environment: THREE.Texture | null = null;
  private readonly fog: THREE.FogExp2;
  /** Last elevation the dome was built for, quantised. Null until the first build. */
  private builtFor: number | null = null;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly renderer: Renderer,
  ) {
    // Daylight until the sun model says otherwise, so the one frame that may render before the first
    // `update` lands is the ordinary case rather than a black sky.
    this.gradient = createGradientTexture(DAY.palette);
    this.fog = new THREE.FogExp2(0xc3dcf0, FOG_DENSITY);

    this.scene.background = this.gradient;
    this.scene.fog = this.fog;
  }

  /**
   * Bring the sky in step with the sun.
   *
   * Safe to call every frame: the gradient's pixels are rewritten only when the quantised elevation
   * moves, and the expensive prefilter only when the pixels did.
   */
  update(state: SkyState): void {
    const quantised =
      Math.round(state.elevationDegrees / REBUILD_STEP_DEGREES) * REBUILD_STEP_DEGREES;
    if (this.builtFor === quantised) return;
    this.builtFor = quantised;

    const palette = paletteFor(quantised);

    // A new texture object, rather than new pixels in the existing one.
    //
    // `Scene.background` holding an equirectangular texture is not sampled directly: three converts
    // it to a cube render target once, in `WebGLCubeMaps.get`, and caches that conversion in a
    // WeakMap keyed by the texture. The only thing that evicts the entry is the source texture's
    // `dispose` event. So rewriting the pixels and setting `needsUpdate` — which is enough for every
    // other texture, and is enough here for the PMREM prefilter below, which reads the source each
    // time — updated the fog and the fill and left the *sky itself* on whatever hour the first frame
    // was drawn at. Rendering the background at 21:30 came back byte-for-byte identical to noon.
    //
    // Disposing the old texture is therefore load-bearing rather than tidiness: it is what drops the
    // stale cube map. The cost is a 32x128 texture and one small cube conversion per rebuild, and
    // rebuilds are already quantised to 2 degrees of elevation.
    const gradient = createGradientTexture(palette);
    this.scene.background = gradient;
    this.gradient.dispose();
    this.gradient = gradient;

    this.fog.color.copy(palette.horizon);
    this.scene.environmentIntensity = palette.intensity;

    // Replaced rather than updated: a PMREM cube map is the *output* of a convolution, so there is
    // nothing to re-upload into — the source has to be run through the generator again. The previous
    // one is disposed after the new one exists, so a failed prefilter leaves the old sky rather than
    // an unlit scene.
    const next = this.renderer.prefilterEnvironment?.(gradient) ?? null;
    if (!next) return;
    this.environment?.dispose();
    this.environment = next;
    this.scene.environment = next;
  }

  dispose(): void {
    this.scene.background = null;
    this.scene.environment = null;
    this.scene.fog = null;
    this.environment?.dispose();
    this.environment = null;
    this.gradient.dispose();
  }
}

/** Interpolate the keyframes at an elevation, clamped at both ends. */
export function paletteFor(elevationDegrees: number): SkyPalette {
  let lower = NIGHT;
  let upper = NIGHT;
  for (const frame of KEYFRAMES) {
    if (frame.elevation <= elevationDegrees) {
      lower = frame;
      upper = frame;
      continue;
    }
    upper = frame;
    break;
  }

  const span = upper.elevation - lower.elevation;
  if (span <= 0) return lower.palette;
  const t = (elevationDegrees - lower.elevation) / span;
  return {
    zenith: lower.palette.zenith.clone().lerp(upper.palette.zenith, t),
    horizon: lower.palette.horizon.clone().lerp(upper.palette.horizon, t),
    ground: lower.palette.ground.clone().lerp(upper.palette.ground, t),
    intensity: lower.palette.intensity + (upper.palette.intensity - lower.palette.intensity) * t,
  };
}

function createGradientTexture(palette: SkyPalette): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    new Uint8Array(SKY_WIDTH * SKY_HEIGHT * 4),
    SKY_WIDTH,
    SKY_HEIGHT,
  );
  texture.name = 'sky-gradient';
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  // Equirectangular so the same texture serves as the background dome and as the PMREM source.
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  writeGradient(texture, palette);
  return texture;
}

/**
 * Paint the ramp into an equirectangular texture. Row 0 is the zenith and the last row is straight
 * down, so the horizon is the middle row and `v` is the sine of the view elevation, not the angle.
 *
 * The zenith→horizon half is eased with a square *root*, which is the opposite of the intuitive
 * choice and was arrived at by looking at it. The eye-level camera this tool is used from sees
 * almost none of the dome — a frame is sky from the horizon to perhaps 25° up — so an easing that
 * holds the zenith blue high in the dome puts none of it on screen, and the whole sky renders as the
 * pale horizon band. The root pulls the blue down to where the frame actually is: at 10° above the
 * horizon the ramp is already a third of the way to zenith.
 */
function writeGradient(texture: THREE.DataTexture, palette: SkyPalette): void {
  const data = texture.image.data as Uint8Array;
  const color = new THREE.Color();
  // Stops are interpolated in the working (linear) space, which is where a blend between two
  // colours is physically a blend, and then encoded on the way out — the texture is tagged sRGB, so
  // writing working-space components straight into it would be decoded a second time on sampling
  // and the whole sky would come out roughly half as bright as its stops.
  const encoded = { r: 0, g: 0, b: 0 };

  for (let y = 0; y < SKY_HEIGHT; y += 1) {
    // -1 (straight down) .. +1 (straight up).
    const up = 1 - (2 * (y + 0.5)) / SKY_HEIGHT;
    if (up >= 0) {
      color.copy(palette.horizon).lerp(palette.zenith, Math.sqrt(up));
    } else {
      // Below the horizon: a short fade into the ground haze, then flat. Nothing looks at this
      // directly — the terrain covers it — but it is half of what the environment integrates, and a
      // dome that is bright all the way round lights an object's underside as hard as its top.
      color.copy(palette.horizon).lerp(palette.ground, Math.min(1, -up * 3));
    }

    color.getRGB(encoded, THREE.SRGBColorSpace);
    const r = Math.round(encoded.r * 255);
    const g = Math.round(encoded.g * 255);
    const b = Math.round(encoded.b * 255);
    for (let x = 0; x < SKY_WIDTH; x += 1) {
      const i = (y * SKY_WIDTH + x) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  texture.needsUpdate = true;
}
